import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DNS,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.replayIntegration(),
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration()
  ],

  tracesSampleRate: 1.0,
  tracePropagationTargets: [/^\//, /^https:\/\/yourserver\.io\/api/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  autoSessionTracking: true,

  beforeSend(event, hint) {
    if (typeof window !== 'undefined') {
      event.contexts = {
        ...event.contexts,
        url_params: {
          search: window.location.search,
          pathname: window.location.pathname,
          hash: window.location.hash
        }
      }
    }

    // Enhanced tracking for removeChild errors
    const error = hint.originalException || hint.syntheticException
    const errorMessage = error instanceof Error ? error.message : String(error)

    if (errorMessage.includes('removeChild') || errorMessage.includes('Node')) {
      // Add special tag for removeChild errors
      event.tags = {
        ...event.tags,
        error_type: 'dom_removeChild',
        investigation: 'modal_portal_race_condition'
      }

      // Capture DOM state
      event.contexts = {
        ...event.contexts,
        dom_state: {
          chakra_portals: document.querySelectorAll('.chakra-portal').length,
          modals_count: document.querySelectorAll('[role="dialog"]').length,
          body_children: document.body.children.length,
          has_chakra_modal: document.querySelector('.chakra-modal__content') !== null
        }
      }

      // Add fingerprint to group all removeChild errors together
      event.fingerprint = ['{{ default }}', 'removeChild-modal-error']
    }

    if (process.env.NODE_ENV === 'development') {
      console.error('Sentry captured error:', hint.originalException || hint.syntheticException)
    }

    return event
  },

  ignoreErrors: ['top.GLOBALS', 'originalCreateNotification', 'canvas.contentDocument', 'MyApp_RemoveAllHighlights', 'fb_xd_fragment', 'bmi_SafeAddOnload', 'EBCallBackMessageReceived', 'conduitPage', 'NetworkError', 'Network request failed', 'Failed to fetch', 'AbortError'],

  beforeSendTransaction(event) {
    if (process.env.NODE_ENV === 'development') {
      return null
    }
    return event
  }
})

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    Sentry.captureException(event.reason, {
      contexts: {
        unhandledRejection: {
          promise: event.promise,
          reason: event.reason
        }
      }
    })
  })

  window.addEventListener('error', (event) => {
    if (!event.error?.__sentryHandled) {
      Sentry.captureException(event.error || event.message, {
        contexts: {
          errorEvent: {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno
          }
        }
      })
    }
  })
}
